import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Login.css'; // Use the new login styles for consistency
import Logo from './common/Logo';
import CountrySelector from './common/CountrySelector';
import { useI18n } from '../i18n/I18nContext';

// Inline CSS for two-column layout
const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
};
const formGridStyleMobile = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

function Register() {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    enterpriseName: '',
    telephone: '',
    country: ''
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [userType, setUserType] = useState('expediteur');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.firstName) {
      errors.firstName = t('register.errors.firstName');
    }
    if (!formData.lastName) {
      errors.lastName = t('register.errors.lastName');
    }
    if (!formData.email) {
      errors.email = t('register.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('register.errors.emailInvalid');
    }
    if (!formData.password) {
      errors.password = t('register.errors.passwordRequired');
    } else if (formData.password.length < 6) {
      errors.password = t('register.errors.passwordMin');
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = t('register.errors.confirmPassword');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('register.errors.passwordMismatch');
    }
    if (!formData.enterpriseName) {
      errors.enterpriseName = t('register.errors.enterpriseName');
    }
    if (!formData.telephone) {
      errors.telephone = t('register.errors.telephone');
    }
    if (!formData.country) {
      errors.country = t('register.errors.fieldRequired', { field: t('client.country') });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleUserTypeChange = (e) => {
    setUserType(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) {
      return;
    }
    try {
      setLoading(true);
      const endpoint =
        userType === 'expediteur'
          ? '/auth/register/expediteur'
          : '/auth/register/transporteur';

      // Both endpoints use @RequestParam — always send multipart/form-data
      const fd = new FormData();
      fd.append('firstname', formData.firstName);
      fd.append('lastname', formData.lastName);
      fd.append('email', formData.email);
      fd.append('password', formData.password);
      fd.append('enterpriseName', formData.enterpriseName);
      fd.append('telephone', formData.telephone);
      if (formData.country) fd.append('country', formData.country);

      await api.post(endpoint, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/login');
    } catch (err) {
      setError(err.message || err.response?.data || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="login-left-panel">
        <div className="left-content">
          <Logo variant="auth" className="left-logo" />
          <h1 className="left-title">BFret</h1>
          <p className="left-tagline">
            {t('marketing.tagline1')}<br />
            <strong>{t('marketing.tagline2')}</strong>
          </p>
        </div>
      </div>
      <div className="register-right-panel">
        <div className="login-card" style={{ maxWidth: 480, minWidth: 320, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflowY: 'visible' }}>
          <h2 className="login-title">{t('register.title')}</h2>
          <p className="login-subtitle">{t('register.subtitle')}</p>
          {error && (
            <div className="alert alert-danger" role="alert">
              <span>{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
            <div style={isMobile ? formGridStyleMobile : formGridStyle}>
              {/* Column 1 */}
              <div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <select
                    className="form-input"
                    value={userType}
                    onChange={handleUserTypeChange}
                    disabled={loading}
                    style={{ paddingRight: '2rem' }}
                  >
                    <option value="expediteur">{t('register.roles.expediteur')}</option>
                    <option value="transporteur">{t('register.roles.transporteur')}</option>
                  </select>
                  <label className="form-label" style={{ top: '-0.7rem', left: '0.8rem', background: '#fff', fontSize: '0.85rem', color: '#1E3455', fontWeight: 600 }}>{t('register.registerAs')}</label>
                </div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="firstName" className="form-label">{t('register.form.firstName')}</label>
                  {validationErrors.firstName && (
                    <div className="error-message">{validationErrors.firstName}</div>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="lastName" className="form-label">{t('register.form.lastName')}</label>
                  {validationErrors.lastName && (
                    <div className="error-message">{validationErrors.lastName}</div>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    id="enterpriseName"
                    name="enterpriseName"
                    value={formData.enterpriseName}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="enterpriseName" className="form-label">{t('register.form.enterpriseName')}</label>
                  {validationErrors.enterpriseName && (
                    <div className="error-message">{validationErrors.enterpriseName}</div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <CountrySelector
                    value={formData.country}
                    onChange={(val) => setFormData(prev => ({ ...prev, country: val }))}
                    label={t('client.country')}
                  />
                  {validationErrors.country && (
                    <div className="error-message">{validationErrors.country}</div>
                  )}
                </div>
              </div>
              {/* Column 2 */}
              <div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="email"
                    className="form-input"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="email" className="form-label">{t('register.form.email')}</label>
                  {validationErrors.email && (
                    <div className="error-message">{validationErrors.email}</div>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="telephone" className="form-label">{t('register.form.telephone')}</label>
                  {validationErrors.telephone && (
                    <div className="error-message">{validationErrors.telephone}</div>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="password"
                    className="form-input"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="password" className="form-label">{t('register.form.password')}</label>
                  {validationErrors.password && (
                    <div className="error-message">{validationErrors.password}</div>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                  <input
                    type="password"
                    className="form-input"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={loading}
                    required
                  />
                  <label htmlFor="confirmPassword" className="form-label">{t('register.form.confirmPassword')}</label>
                  {validationErrors.confirmPassword && (
                    <div className="error-message">{validationErrors.confirmPassword}</div>
                  )}
                </div>
              </div>
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={loading}
              style={{ marginTop: '1.2rem' }}
            >
              {loading ? t('register.registering') : t('register.submit')}
            </button>
          </form>
          <div className="divider">
            <span className="divider-text">{t('register.alreadyHaveAccount')}</span>
          </div>
          <div className="signup-section">
            <Link to="/login" className="signup-link">
              {t('auth.signIn')}
            </Link>
          </div>
          <div className="form-footer">
            <p>
              {t('register.agreeTo')}{' '}
              <Link to="/terms" className="footer-link">{t('auth.terms')}</Link>
              {' '}{t('register.and')}{' '}
              <Link to="/privacy" className="footer-link">{t('auth.privacyPolicy')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register; 